import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip as RechartsTooltip, Cell } from 'recharts';
import { Users, Mail, Globe, TrendingUp, CheckCircle2, PlusSquare, UserPlus, Edit3, Sparkles } from 'lucide-react';
import { cn } from '../../infrastructure/lib/utils';
import { useFormat } from '@/src/infrastructure/lib/format';
import { trackEvent } from '../../infrastructure/lib/analytics';

import { StatCard } from '../components/ui/StatCard';
import { ChartCard } from '../components/ui/ChartCard';
import {
  getDashboardStats,
  getRecentInvitations,
  getRecentUsers,
  getUserGrowth,
  type AdminInvitation,
  type AdminUser,
  type DashboardStats,
  type UserGrowthPoint,
} from '../../infrastructure/lib/api';

const iconsMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Users, Mail, Globe, TrendingUp,
};

const activityIcons: Record<string, any> = {
  rsvp: { icon: CheckCircle2, color: 'text-electric-violet', bg: 'bg-electric-violet/20' },
  create: { icon: PlusSquare, color: 'text-tertiary-container', bg: 'bg-tertiary-container/20' },
  user: { icon: UserPlus, color: 'text-primary', bg: 'bg-primary/20' },
  edit: { icon: Edit3, color: 'text-on-surface-variant', bg: 'bg-on-surface-variant/20' },
};

function buildStatsCards(stats: DashboardStats | null, fmtNumber: (n: number) => string) {
  const totalRsvps = (stats?.rsvpAnalytics ?? []).reduce(
    (sum, r) => sum + r._count.status,
    0,
  );
  const accepted =
    stats?.rsvpAnalytics?.find((r) => r.status === 'ACCEPTED')?._count.status ?? 0;
  const acceptRate = totalRsvps > 0 ? Math.round((accepted / totalRsvps) * 100) : 0;

  return [
    {
      label: 'Total Users',
      value: stats ? fmtNumber(stats.totalUsers) : '—',
      change: '',
      progress: 75,
      icon: 'Users',
      color: 'primary',
    },
    {
      label: 'Total Invitations',
      value: stats ? fmtNumber(stats.totalInvitations) : '—',
      change: '',
      progress: 50,
      icon: 'Mail',
      color: 'tertiary-container',
    },
    {
      label: 'Media Assets',
      value: stats ? fmtNumber(stats.totalMedia) : '—',
      change: '',
      progress: 80,
      icon: 'Globe',
      color: 'electric-violet',
    },
    {
      label: 'RSVP Accept Rate',
      value: `${acceptRate}%`,
      change: `${totalRsvps} total`,
      progress: acceptRate,
      icon: 'TrendingUp',
      color: 'primary',
    },
  ];
}

interface ActivityItem {
  id: string;
  title: string;
  description: string;
  time: string;
  type: 'rsvp' | 'create' | 'user' | 'edit';
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
}

function buildActivity(
  invitations: AdminInvitation[],
  users: AdminUser[],
): ActivityItem[] {
  const i: ActivityItem[] = invitations.slice(0, 5).map((inv) => ({
    id: `inv-${inv.id}`,
    title: 'Invitation created',
    description: `${inv.eventName} (${inv.occasionType}) by ${inv.user.name}`,
    time: timeAgo(inv.createdAt),
    type: 'create',
  }));
  const u: ActivityItem[] = users.slice(0, 5).map((user) => ({
    id: `usr-${user.id}`,
    title: 'New user registered',
    description: `${user.name} — ${user.email}`,
    time: timeAgo(user.createdAt),
    type: 'user',
  }));
  return [...i, ...u]
    .sort((a, b) => a.time.localeCompare(b.time))
    .slice(0, 8);
}

/** Convert API growth points to chart-ready format, filling gaps with 0. */
function buildChartData(points: UserGrowthPoint[], days: 30 | 90) {
  if (points.length === 0) return [];

  // For display, use abbreviated month-day labels
  return points.map((p) => {
    const d = new Date(p.date);
    const label =
      days <= 30
        ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return { name: label, value: p.count };
  });
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentInvitations, setRecentInvitations] = useState<AdminInvitation[]>([]);
  const [recentUsers, setRecentUsers] = useState<AdminUser[]>([]);
  const [growthDays, setGrowthDays] = useState<30 | 90>(30);
  const [growthData, setGrowthData] = useState<UserGrowthPoint[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackEvent('ADMIN_DASHBOARD_VIEW', 'engagement');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, inv, usr] = await Promise.all([
          getDashboardStats(),
          getRecentInvitations(),
          getRecentUsers(),
        ]);
        if (cancelled) return;
        setStats(s);
        setRecentInvitations(inv);
        setRecentUsers(usr);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getUserGrowth(growthDays)
      .then((pts) => { if (!cancelled) setGrowthData(pts); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [growthDays]);

  const fmt = useFormat();
  const statsCards = useMemo(
    () => buildStatsCards(stats, fmt.number),
    [stats, fmt.number],
  );
  const activity = useMemo(
    () => buildActivity(recentInvitations, recentUsers),
    [recentInvitations, recentUsers],
  );
  const chartData = useMemo(
    () => buildChartData(growthData, growthDays),
    [growthData, growthDays],
  );

  const topTemplates = stats?.topTemplates ?? [];
  const trendingTemplate = topTemplates[0] ?? null;

  return (
    <div className="space-y-8">
      {error && (
        <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}
      {/* Header Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-4xl font-bold">Analytics Overview</h2>
          <p className="text-on-surface-variant mt-1">Real-time performance metrics for your invitation engine.</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, i) => {
          const Icon = iconsMap[stat.icon];
          // Map colorTheme → icon color class so each metric gets its own hue.
          // Without this explicit mapping, StatCard defaults to 'text-primary' for all icons.
          const iconColorMap: Record<string, string> = {
            'primary': 'text-primary',
            'tertiary-container': 'text-tertiary-container',
            'electric-violet': 'text-electric-violet',
          };
          const iconColor = iconColorMap[stat.color] ?? 'text-primary';
          return (
            <StatCard
              key={stat.label}
              label={stat.label}
              value={stat.value}
              icon={Icon}
              color={iconColor}
              change={stat.change}
              progress={stat.progress}
              colorTheme={stat.color as 'primary' | 'tertiary-container' | 'electric-violet'}
              delay={i * 0.1}
            />
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Growth Chart — real data from GET /admin/dashboard/user-growth */}
        <ChartCard
          title="User Growth"
          description="New user registrations over time"
          className="lg:col-span-2"
          headerAction={
            <select
              value={growthDays}
              onChange={(e) => setGrowthDays(Number(e.target.value) as 30 | 90)}
              className="bg-surface-container-high border-none text-xs font-bold rounded-lg px-3 py-1.5 text-on-surface focus:ring-1 focus:ring-primary outline-none"
            >
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
          }
        >
          {chartData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-on-surface-variant text-sm">
              No registrations in this period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#c7c4d7', fontSize: 10 }}
                  dy={10}
                  interval="preserveStartEnd"
                />
                <RechartsTooltip
                  cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                  contentStyle={{ backgroundColor: '#1c1b1d', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={index === chartData.length - 1 ? '#8B5CF6' : '#c0c1ff'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Recent Activity */}
        <div className="glass-panel rounded-3xl overflow-hidden flex flex-col">
          <div className="p-6 border-b border-border-subtle">
            <h4 className="font-display text-xl font-bold leading-none">Recent Activity</h4>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
            {activity.map((item, i) => {
              const config = activityIcons[item.type];
              const Icon = config.icon;
              return (
                <div key={item.id} className="flex gap-4 relative">
                  {i !== activity.length - 1 && (
                    <div className="absolute top-10 left-5 w-[1px] h-[calc(100%+24px)] bg-border-subtle" />
                  )}
                  <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0", config.bg, config.color)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-sm text-on-surface font-semibold leading-tight">{item.title}</p>
                    <p className="text-xs text-on-surface-variant leading-tight">{item.description}</p>
                    <p className="text-[10px] text-on-surface-variant font-bold uppercase tracking-tight opacity-60 mt-1">{item.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Featured Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Popular Templates — real data from dashboardStats.topTemplates */}
        <div className="glass-panel p-6 rounded-3xl">
          <h4 className="font-display text-xl font-bold leading-none mb-6">Popular Templates</h4>
          {topTemplates.length === 0 ? (
            <p className="text-sm text-on-surface-variant">No templates used yet.</p>
          ) : (
            <div className="space-y-6">
              {topTemplates.map((t) => {
                const barColors = ['bg-electric-violet', 'bg-primary', 'bg-tertiary-container', 'bg-on-surface-variant', 'bg-surface-container-high'];
                const colorIdx = topTemplates.indexOf(t) % barColors.length;
                return (
                  <div key={t.templateId} className="space-y-2">
                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                      <span className="truncate max-w-[70%]">{t.name}</span>
                      <span>{t.sharePercent}%</span>
                    </div>
                    <div className="h-2 w-full bg-surface-container rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${t.sharePercent}%` }}
                        transition={{ duration: 1, delay: 1 }}
                        className={cn("h-full", barColors[colorIdx])}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Trending template card — uses top template's previewImageUrl */}
        {trendingTemplate ? (
          <div className="glass-panel rounded-3xl overflow-hidden lg:col-span-2 group cursor-pointer relative min-h-[300px]">
            {trendingTemplate.previewImageUrl ? (
              <img
                src={trendingTemplate.previewImageUrl}
                alt={trendingTemplate.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
              />
            ) : (
              <div className="absolute inset-0 ai-shimmer" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-8 flex flex-col justify-end">
              <span className="bg-electric-violet text-white text-[10px] font-bold px-2 py-1 rounded-full w-fit mb-2 tracking-widest leading-none">TRENDING NOW</span>
              <h3 className="text-3xl font-display font-bold text-white mb-2 leading-none">{trendingTemplate.name}</h3>
              <p className="text-white/70 text-sm max-w-md">
                Most used template this period — {trendingTemplate.invitationCount} invitation{trendingTemplate.invitationCount !== 1 ? 's' : ''} created.
              </p>
            </div>
          </div>
        ) : (
          <div className="glass-panel rounded-3xl overflow-hidden lg:col-span-2 flex items-center justify-center min-h-[300px]">
            <p className="text-on-surface-variant text-sm">No template data yet.</p>
          </div>
        )}
      </div>

      {/* AI Credit Usage & Increased Costing Dashboard Panel */}
      <div className="glass-panel p-8 rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-transparent to-orange-500/5 space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-amber-500/20 pb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="bg-gradient-to-r from-amber-500 to-orange-600 text-white text-[10px] font-black uppercase px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
                <Sparkles className="w-3 h-3 text-amber-200 animate-pulse" />
                ⚡ Premium AI Generation Tier
              </span>
            </div>
            <h3 className="font-display text-2xl font-bold text-on-surface">AI Credit Usage & Cost Analytics</h3>
            <p className="text-xs text-on-surface-variant">
              Track Gemini image generation credits, pipeline execution costs, and template-based credit consumption.
            </p>
          </div>
          <div className="flex items-center gap-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl px-5 py-3">
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400">Est. API Spend</p>
              <p className="text-xl font-bold font-mono text-amber-500">${stats?.aiCreditStats?.totalCostUsd?.toFixed(2) ?? '0.00'}</p>
            </div>
          </div>
        </div>

        {/* AI Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10 flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Total AI Credits</span>
            <span className="text-3xl font-bold font-display text-amber-400">{stats?.aiCreditStats?.totalCreditsUsed ?? 0}</span>
            <span className="text-xs text-on-surface-variant/70">3 Credits / AI Generation Run</span>
          </div>
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10 flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">AI Pipeline Runs</span>
            <span className="text-3xl font-bold font-display text-primary">{stats?.aiCreditStats?.totalJobs ?? 0}</span>
            <span className="text-xs text-green-400 font-semibold">{stats?.aiCreditStats?.completedJobs ?? 0} Complete • {stats?.aiCreditStats?.failedJobs ?? 0} Failed</span>
          </div>
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10 flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Cost per AI Job</span>
            <span className="text-3xl font-bold font-display text-tertiary-container">$0.15</span>
            <span className="text-xs text-on-surface-variant/70">Gemini 2.0 Flash Rate</span>
          </div>
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10 flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Active AI Templates</span>
            <span className="text-3xl font-bold font-display text-electric-violet">{stats?.aiCreditStats?.byTemplate?.length ?? 0}</span>
            <span className="text-xs text-amber-400 font-semibold">High Tier Pricing Active</span>
          </div>
        </div>

        {/* AI Recent Job Execution Logs Table */}
        <div className="space-y-4">
          <h4 className="font-display text-lg font-bold text-on-surface flex items-center gap-2">
            <span>Recent AI Generation Credit Logs</span>
          </h4>
          {(!stats?.aiCreditStats?.recentJobs || stats.aiCreditStats.recentJobs.length === 0) ? (
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 text-center text-sm text-on-surface-variant">
              No AI credit generation jobs recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                    <th className="pb-3">Invitation / User</th>
                    <th className="pb-3">Template</th>
                    <th className="pb-3">AI Credits Used</th>
                    <th className="pb-3">Cost (USD)</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-mono">
                  {stats.aiCreditStats.recentJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-3 font-sans">
                        <p className="font-bold text-on-surface">{job.eventName}</p>
                        <p className="text-[10px] text-on-surface-variant">{job.userEmail}</p>
                      </td>
                      <td className="py-3 font-sans font-semibold text-amber-400">{job.templateName}</td>
                      <td className="py-3 font-bold text-amber-400">⚡ 3 Credits</td>
                      <td className="py-3 font-bold text-green-400">${job.costUsd.toFixed(4)}</td>
                      <td className="py-3">
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider",
                          job.status === 'COMPLETE' ? "bg-green-500/20 text-green-400 border border-green-500/30" :
                          job.status === 'FAILED' ? "bg-red-500/20 text-red-400 border border-red-500/30" :
                          "bg-amber-500/20 text-amber-400 border border-amber-500/30 animate-pulse"
                        )}>
                          {job.status}
                        </span>
                      </td>
                      <td className="py-3 text-[10px] text-on-surface-variant font-sans">{timeAgo(job.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
